import { CSSProperties } from 'react';
import { useTheme } from '../context/ThemeContext';
import Icon from './Icon';

interface AmountKeypadProps {
  /** Current amount as a raw string, e.g. "" | "25" | "25.50". */
  value: string;
  onChange: (v: string) => void;
  /** Optional ceiling; key presses that would exceed it are ignored. */
  max?: number;
  /** Caption under the figure, e.g. "Min $5 · Max $1,000". */
  hint?: string;
  currency?: string;
  /** Allow a decimal point / cents. Defaults to true. */
  allowDecimal?: boolean;
  /** Stretch to fill the parent: figure centered in the upper slack, keypad
   *  pinned to the bottom (full-screen entry). Off = compact centered cluster. */
  fill?: boolean;
  style?: CSSProperties;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'];

/**
 * Cash App-style amount entry: an oversized "$0" figure driven by a custom
 * numeric keypad (no OS keyboard). Enforces a single decimal point, max two
 * decimal places, and an optional `max` ceiling.
 */
export default function AmountKeypad({
  value,
  onChange,
  max,
  hint,
  currency = '$',
  allowDecimal = true,
  fill = false,
  style,
}: AmountKeypadProps) {
  const { colors } = useTheme();

  const press = (k: string) => {
    let next = value;
    if (k === 'back') {
      next = value.slice(0, -1);
    } else if (k === '.') {
      if (!allowDecimal || value.includes('.')) return;
      next = value === '' ? '0.' : value + '.';
    } else {
      if (value.includes('.')) {
        const dec = value.split('.')[1] ?? '';
        if (dec.length >= 2) return; // cap at cents
      }
      next = value === '0' ? k : value + k;
    }
    if (max != null && next !== '' && parseFloat(next) > max) return;
    onChange(next);
  };

  const empty = value === '';
  const display = empty ? '0' : value;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...(fill ? { flex: 1, alignSelf: 'stretch', justifyContent: 'space-between' } : {}),
        ...style,
      }}
    >
      {/* Figure (+hint). In fill mode this region eats the slack so the amount
          sits centered above a bottom-pinned keypad; otherwise it's inline. */}
      <div
        style={
          fill
            ? { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'center' }
            : { display: 'contents' }
        }
      >
        <div
          className="sls-tnum"
          style={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1, marginBottom: hint ? 4 : 18 }}
        >
          <span
            style={{
              fontSize: 34,
              fontWeight: 800,
              marginTop: 9,
              color: empty ? colors.textMuted : colors.text,
            }}
          >
            {currency}
          </span>
          <span
            style={{
              fontSize: 72,
              fontWeight: 800,
              letterSpacing: -2,
              color: empty ? colors.textMuted : colors.text,
            }}
          >
            {display}
          </span>
        </div>
        {hint && <div style={{ fontSize: 14, color: colors.textSub, marginBottom: 18 }}>{hint}</div>}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 4,
          width: '100%',
          maxWidth: fill ? 360 : 320,
          marginBottom: fill ? 8 : 0,
        }}
      >
        {KEYS.map(k => {
          const blank = k === '.' && !allowDecimal;
          return (
            <button
              key={k}
              className={blank ? undefined : 'sls-press'}
              onClick={() => { if (!blank) press(k); }}
              disabled={blank}
              aria-label={k === 'back' ? 'Delete' : k}
              style={{
                height: fill ? 70 : 62,
                border: 'none',
                background: 'transparent',
                fontSize: fill ? 28 : 26,
                fontWeight: 700,
                color: colors.text,
                cursor: blank ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'inherit',
              }}
            >
              {k === 'back' ? <Icon name="delete-left" size={24} color={colors.text} /> : blank ? '' : k}
            </button>
          );
        })}
      </div>
    </div>
  );
}
